import { RootState, AppDispatch } from '@share/utils';
import React from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import { IReviewPhoto } from '@common-types';

import { getActivityReviews, getActivityTravelerPhotos, activityReviewsActions } from '@share/store/slices';
import { TravelerPhotos } from './traveler-photos/component';
import { RatingSummary } from './rating-summary/component';
import { ReviewSearchSort } from './review-search-sort/component';
import { ReviewItem } from './review-Item/component';
import { ReviewSkeleton } from './review-skeleton/component';
import { PhotoGalleryModal } from './photo-modal/component';
import './style.scss';

interface ReviewsProps {
    productCode: string;
}

export interface IPhotoWithReview {
    photo: IReviewPhoto;
    review: {
        userName: string;
        publishedDate: string;
        rating: number;
        title: string;
        text: string;
    };
}

export const Reviews: React.FC<ReviewsProps> = ({ productCode }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { reviews, summary, loading, sortBy, isFiltering, travelerPhotos, searchText, hasMore } = useSelector((state: RootState) => state.activityReviewsStore);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = React.useState<number | null>(null);

    const fetchReviews = (reset = false) => {
        dispatch(getActivityReviews({ productCode, reset }));
    };

    const fetchTravelerPhotos = () => {
        dispatch(getActivityTravelerPhotos(productCode));
    };

    React.useEffect(() => {
        dispatch(activityReviewsActions.resetReviews());
        fetchReviews(true);
        fetchTravelerPhotos();
    }, [productCode]);

    const handleSortChange = (newSort: any) => {
        dispatch(activityReviewsActions.setSortBy(newSort));
        dispatch(getActivityReviews({ productCode, reset: true }));
    };

    const handleShowMore = () => {
        dispatch(activityReviewsActions.incrementPage());
        dispatch(getActivityReviews({ productCode, reset: false }));
    };

    const handleSearch = React.useCallback(
        debounce((value: string) => {
            dispatch(activityReviewsActions.setSearchText(value));
            dispatch(getActivityReviews({ productCode, reset: true }));
        }, 1000),
        [dispatch, productCode]
    );

    // Extract all photos from travelerPhotos
    const allPhotos: IPhotoWithReview[] = React.useMemo(() => {
        return travelerPhotos.reduce((acc: IPhotoWithReview[], review) => {
            if (review.photos) {
                review.photos.forEach(photo => {
                    acc.push({
                        photo,
                        review: {
                            userName: review.userName,
                            publishedDate: review.publishedDate,
                            rating: review.rating,
                            title: review.title,
                            text: review.text
                        }
                    });
                });
            }
            return acc;
        }, []);
    }, [travelerPhotos]);

    if (!summary && loading) return <ReviewSkeleton />;

    if (!loading && reviews.length === 0 && allPhotos.length === 0 && !searchText) return null;

    return (
        <div className="reviews-container" id="reviews-section">
            <TravelerPhotos photos={allPhotos} onPhotoClick={setSelectedPhotoIndex} />

            {(reviews.length > 0 || searchText) && (
                <>
                    {reviews.length > 0 && <h3 className="reviews-title">Reviews</h3>}

                    {summary && (
                        <RatingSummary
                            averageRating={summary.averageRating}
                            totalReviews={summary.totalReviews}
                            ratingCounts={summary.reviewCountTotals}
                            sources={summary.sources}
                        />
                    )}

                    <ReviewSearchSort
                        sortBy={sortBy}
                        onSortChange={handleSortChange}
                        onSearch={handleSearch}
                        initialSearchText={searchText}
                    />

                    <div className="reviews-list">
                        {isFiltering ? (
                            [1, 2, 3].map((i) => (
                                <div key={`filter-skeleton-${i}`} className="review-item-skeleton">
                                    <Skeleton active avatar title paragraph={{ rows: 3 }} />
                                </div>
                            ))
                        ) : (
                            <>
                                {reviews.map((review, index) => (
                                    <ReviewItem
                                        key={review.reviewReference || index}
                                        review={review}
                                        onPhotoClick={(photoUrl) => {
                                            const globalIndex = allPhotos.findIndex(p => p.photo.versions.some(v => v.url === photoUrl));
                                            if (globalIndex !== -1) setSelectedPhotoIndex(globalIndex);
                                        }}
                                    />
                                ))}

                                {loading && [1, 2].map((i) => (
                                    <div key={`more-skeleton-${i}`} className="review-item-skeleton">
                                        <Skeleton active avatar title paragraph={{ rows: 3 }} />
                                    </div>
                                ))}

                                {reviews.length === 0 && searchText && !loading && (
                                    <div className="no-reviews-found">
                                        No review found with this search
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {hasMore && reviews.length > 0 && (
                        <button className="show-more-button" onClick={handleShowMore} disabled={loading}>
                            {loading ? 'Loading...' : `Show 10 more reviews`}
                        </button>
                    )}
                </>
            )}



            {selectedPhotoIndex !== null && (
                <PhotoGalleryModal
                    photos={allPhotos}
                    initialIndex={selectedPhotoIndex}
                    onClose={() => setSelectedPhotoIndex(null)}
                />
            )}
        </div>
    );
};
